import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DwEventService, DwMetaDataFormApi, DwSecurityStateService, DwSecurityTopics } from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';
import { AppMetaDataItemNames, DocumentItemEntity } from '../../../meta-data/app-meta-data.service';

type TraceDetails = { [key: string]: string | number | boolean | null | undefined };

/** Opt-in, tab-scoped diagnostics. Never records security contexts or form payloads. */
@Injectable({ providedIn: 'root' })
export class DocumentViewerTraceService implements OnDestroy {
  readonly enabled: boolean;
  private readonly subscriptions = new Subscription();
  private readonly restoreMethods: Array<() => void> = [];
  private readonly observedForms = new WeakSet<DwMetaDataFormApi>();
  private readonly startedAt = Date.now();
  private viewerCount = 0;
  private readonly onVisibilityChange = () => this.record('page.visibility', { visibility: document.visibilityState });

  constructor(private readonly router: Router,
    private readonly securityStateService: DwSecurityStateService,
    private readonly eventService: DwEventService) {
    this.enabled = this.resolveEnabled();
    if (!this.enabled) {
      return;
    }

    this.record('trace.started');
    this.subscriptions.add(this.securityStateService.securityStateObservable.subscribe(state => {
      this.record('security.state', {
        authenticated: !!state?.isAuthenticated,
        authenticating: !!state?.isAuthenticating,
        refreshing: !!state?.isRefreshing
      });
    }));
    this.subscriptions.add(this.securityStateService.securityContextChanged().subscribe(context => {
      const expiration = Date.parse(String(context?.SecurityTokenExpirationDateTime || ''));
      this.record('security.context-changed', {
        hasContext: !!context,
        tokenExpiresInSeconds: Number.isFinite(expiration) ? Math.round((expiration - Date.now()) / 1000) : null
      }, true);
    }));
    this.subscriptions.add(this.eventService.getEvent(DwSecurityTopics.SECURITY_CHANGED)
      .subscribe(() => this.record('security.cache-invalidation', {}, true)));
    this.subscriptions.add(this.router.events.subscribe(event => {
      const name = event.constructor.name;
      if (['NavigationStart', 'NavigationEnd', 'NavigationCancel', 'NavigationError'].includes(name)) {
        const url = 'url' in event && typeof event.url === 'string' ? event.url : '';
        this.record(`router.${name}`, { resourceNavigation: url.startsWith('/resources/') });
      }
    }));
    this.wrapMethod(this.router, 'resetConfig', 'router.reset-config', () => ({}));
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  nextViewerId(): number {
    return ++this.viewerCount;
  }

  observeForm(formApi?: DwMetaDataFormApi): void {
    if (!this.enabled || !formApi || formApi.item !== AppMetaDataItemNames.DocumentItem || this.observedForms.has(formApi)) {
      return;
    }
    this.observedForms.add(formApi);
    const details = (): TraceDetails => {
      try {
        const data = formApi.getFormData<DocumentItemEntity>();
        const rawData = (formApi as DwMetaDataFormApi & { data?: DocumentItemEntity }).data;
        return {
          formItem: formApi.item, dataLoaded: !!formApi.isDataLoadComplete?.value,
          hasDocumentId: !!data?.DocumentItemId, hasDocumentType: !!data?.DocumentTypeId,
          hasMarkup: !!data?.EmbeddedMarkup, rawDataHasDocumentId: !!rawData?.DocumentItemId,
          rawDataHasDocumentType: !!rawData?.DocumentTypeId
        };
      } catch {
        return { formItem: formApi.item, formReadFailed: true };
      }
    };
    // These methods are present on the metadata form implementing the public API.
    // setupConfig is the key checkpoint: CAP clears form data during that call.
    for (const method of ['setupConfig', 'loadData', 'initialDataLoad']) {
      this.wrapMethod(formApi, method, `form.${method}`, details);
    }
    this.subscriptions.add(formApi.isDataLoadComplete.subscribe(loaded => this.record('form.data-loaded', { loaded })));
  }

  record(event: string, details: TraceDetails = {}, includeStack = false): void {
    if (!this.enabled) {
      return;
    }
    // Only explicit scalar diagnostics reach the console; no tokens, markup or entity dumps.
    console.info('[Document viewer trace]', JSON.stringify({
      at: new Date().toISOString(), elapsedMs: Date.now() - this.startedAt,
      event, ...details, ...(includeStack ? { stack: new Error().stack } : {})
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.restoreMethods.reverse().forEach(restore => restore());
    if (this.enabled) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  private resolveEnabled(): boolean {
    const option = new URLSearchParams(window.location.search).get('traceDocumentViewer');
    const key = 'aviator.document-viewer-trace';
    try {
      if (option === '0') {
        window.sessionStorage.removeItem(key);
        return false;
      }
      if (option === '1') {
        window.sessionStorage.setItem(key, '1');
      }
      return option === '1' || window.sessionStorage.getItem(key) === '1';
    } catch {
      return option === '1';
    }
  }

  private wrapMethod(target: object, name: string, event: string, details: () => TraceDetails): void {
    const methods = target as { [key: string]: unknown };
    const original = methods[name];
    if (typeof original !== 'function') {
      return;
    }
    const descriptor = Object.getOwnPropertyDescriptor(target, name);
    const trace = this;
    const wrapped = function(this: unknown, ...args: unknown[]) {
      trace.record(event, details(), true);
      try {
        const result = original.apply(this, args);
        trace.record(`${event}.returned`, details());
        return result;
      } catch (error) {
        trace.record(`${event}.threw`);
        throw error;
      }
    };
    methods[name] = wrapped;
    this.restoreMethods.push(() => {
      if (methods[name] !== wrapped) {
        return;
      }
      if (descriptor) {
        Object.defineProperty(target, name, descriptor);
      } else {
        delete methods[name];
      }
    });
  }
}
